import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Sparkles, SlidersHorizontal, Brain } from "lucide-react";
import { ResultRow } from "./constants";
import RankingTable from "./RankingTable";
import RaioXBicho from "./RaioXBicho";
import PalpitesAvancadosCards from "./PalpitesAvancadosCards";

import {
  AdvancedFilters,
  DIAS_SEMANA,
  FAIXAS_PREMIO,
  MODALIDADES,
  Modalidade,
  analisar,
  buildSorteios,
  gerarInsights,
  ordenar,
  rotuloItem,
} from "./analiseEngine";

interface AnaliseAvancadaProps {
  results: ResultRow[];
  filtrosGeracao: Record<string, unknown>;
}

const TURNOS_RIO = ["PPT", "PTM", "PT", "PTV", "PTN", "COR"];
const TODAS_POSICOES = [1, 2, 3, 4, 5, 6, 7];

const chip = (active: boolean) =>
  `text-xs px-2.5 py-1 rounded-full border transition-colors ${
    active
      ? "bg-yellow-400 text-black border-yellow-300 font-bold shadow-sm shadow-yellow-500/30"
      : "bg-black/40 text-yellow-200/70 border-yellow-500/25 hover:bg-yellow-500/10"
  }`;

const AnaliseAvancada: React.FC<AnaliseAvancadaProps> = ({ results, filtrosGeracao }) => {
  const [aberto, setAberto] = useState(false);
  const [fontes, setFontes] = useState<string[]>(["rio", "federal"]);
  const [faixa, setFaixa] = useState("1-5");
  const [posicoesCustom, setPosicoesCustom] = useState<number[]>([1]);
  const [diasSemana, setDiasSemana] = useState<number[]>([]);
  const [turnos, setTurnos] = useState<string[]>([]);
  const [modalidades, setModalidades] = useState<Modalidade[]>(["grupo", "dezena"]);

  const toggle = <T,>(arr: T[], v: T, set: (x: T[]) => void) =>
    set(arr.includes(v) ? arr.filter((i) => i !== v) : [...arr, v]);

  const filtros: AdvancedFilters = useMemo(() => {
    const f = FAIXAS_PREMIO.find((x) => x.value === faixa);
    const posicoes = faixa === "custom" ? posicoesCustom : f?.posicoes || [];
    return { fontes, posicoes, diasSemana, turnos, modalidades };
  }, [fontes, faixa, posicoesCustom, diasSemana, turnos, modalidades]);

  const sorteios = useMemo(
    () => (aberto ? buildSorteios(results, filtros) : []),
    [aberto, results, filtros]
  );

  const insights = useMemo(() => (aberto ? gerarInsights(sorteios) : []), [aberto, sorteios]);

  const analises = useMemo(
    () =>
      aberto
        ? modalidades.map((m) => ({ mod: m, res: analisar(sorteios, m) }))
        : [],
    [aberto, modalidades, sorteios]
  );

  const totalRegistros = useMemo(
    () => sorteios.reduce((acc, s) => acc + s.rows.length, 0),
    [sorteios]
  );

  return (
    <Card className="border-yellow-500/50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-lg shadow-yellow-500/10">
      <CardHeader className="pb-2 sm:pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-yellow-400">
            <SlidersHorizontal className="h-5 w-5" />
            Análise Avançada
            <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-[10px]">TURBO</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="toggle-avancada" className="text-[11px] text-yellow-200/70">
              {aberto ? "Ativa" : "Ativar"}
            </Label>
            <Switch id="toggle-avancada" checked={aberto} onCheckedChange={setAberto} />
          </div>
        </div>
        <p className="text-[10px] sm:text-xs text-yellow-200/60">
          Combine loteria, prêmios, dias da semana e modalidades para descobrir padrões
        </p>
      </CardHeader>

      {aberto && (
        <CardContent className="space-y-4">
          {/* Loteria */}
          <div>
            <p className="text-[11px] font-semibold text-yellow-300 mb-1.5">Loteria</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: "rio", l: "🎯 Paratodos Rio" },
                { v: "federal", l: "🏛️ Federal" },
              ].map((o) => (
                <button key={o.v} className={chip(fontes.includes(o.v))} onClick={() => toggle(fontes, o.v, setFontes)}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {/* Prêmios */}
          <div>
            <p className="text-[11px] font-semibold text-yellow-300 mb-1.5">Prêmios</p>
            <div className="flex flex-wrap gap-1.5">
              {FAIXAS_PREMIO.map((f) => (
                <button key={f.value} className={chip(faixa === f.value)} onClick={() => setFaixa(f.value)}>
                  {f.label}
                </button>
              ))}
            </div>
            {faixa === "custom" && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TODAS_POSICOES.map((p) => (
                  <button
                    key={p}
                    className={chip(posicoesCustom.includes(p))}
                    onClick={() => toggle(posicoesCustom, p, setPosicoesCustom)}
                  >
                    {p}º
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Turnos — só fazem sentido no Paratodos Rio */}
          {fontes.includes("rio") && (
            <div>
              <p className="text-[11px] font-semibold text-yellow-300 mb-1.5">
                Turnos (Paratodos Rio) — vazio = todos
              </p>
              <div className="flex flex-wrap gap-1.5">
                {TURNOS_RIO.map((t) => (
                  <button key={t} className={chip(turnos.includes(t))} onClick={() => toggle(turnos, t, setTurnos)}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}


          {/* Dias da semana */}
          <div>
            <p className="text-[11px] font-semibold text-yellow-300 mb-1.5">Dias da semana — vazio = todos</p>
            <div className="flex flex-wrap gap-1.5">
              {DIAS_SEMANA.map((d) => (
                <button
                  key={d.value}
                  className={chip(diasSemana.includes(d.value))}
                  onClick={() => toggle(diasSemana, d.value, setDiasSemana)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Modalidades */}
          <div>
            <p className="text-[11px] font-semibold text-yellow-300 mb-1.5">Modalidades para analisar</p>
            <div className="flex flex-wrap gap-1.5">
              {MODALIDADES.map((m) => (
                <button
                  key={m.value}
                  className={chip(modalidades.includes(m.value))}
                  onClick={() => toggle(modalidades, m.value, setModalidades)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Bloco IA */}
          <div className="rounded-xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/10 via-black/40 to-yellow-500/10 p-3">
            <p className="flex items-center gap-2 text-sm font-bold text-yellow-300">
              <Brain className="h-4 w-4" />
              A IA analisou {sorteios.length} sorteios ({totalRegistros} resultados) do Jogo do Bicho
            </p>
            <p className="text-[10px] text-yellow-200/60 mb-2">A inteligência artificial encontrou estes padrões:</p>
            {insights.length === 0 ? (
              <p className="text-xs text-yellow-200/60">Nenhum padrão no recorte atual — amplie os filtros.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {insights.map((i, idx) => (
                  <div key={idx} className="rounded-lg bg-black/40 border border-yellow-500/20 p-2">
                    <p className="text-[10px] font-bold text-yellow-400 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> {i.titulo}
                    </p>
                    <p className="text-[11px] text-yellow-100/90 leading-snug">{i.texto}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <PalpitesAvancadosCards
            sorteios={sorteios}
            fontes={fontes}
            filtros={{
              ...filtrosGeracao,
              faixa,
              posicoes: filtros.posicoes,
              diasSemana,
              turnos,
              modalidades,
              total_sorteios_analisados: sorteios.length,
              total_resultados_analisados: totalRegistros,
            }}
          />

          {/* Raio-X do Bicho */}
          <RaioXBicho sorteios={sorteios} />

          {/* Rankings */}

          {analises.map(({ mod, res }) => {
            const label = MODALIDADES.find((m) => m.value === mod)?.label || mod;
            return (
              <div key={mod} className="space-y-2">
                <p className="text-sm font-bold text-yellow-400 border-b border-yellow-500/20 pb-1">
                  {label} — {res.totalSorteios} sorteios analisados
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  <RankingTable titulo={`🔥 ${label} mais sorteados`} itens={ordenar(res.itens, "mais")} destaque="freq" />
                  <RankingTable titulo={`⏳ ${label} mais atrasados`} itens={ordenar(res.itens, "atraso")} destaque="atraso" />
                  <RankingTable titulo={`❄️ ${label} menos sorteados`} itens={ordenar(res.itens, "menos")} destaque="freq" />
                  <RankingTable titulo={`📈 ${label} em alta`} itens={ordenar(res.itens, "alta")} destaque="tendencia" />
                </div>
                {res.nuncaSorteados.length > 0 && (
                  <div className="rounded-lg border border-yellow-500/20 bg-black/30 p-2 sm:p-3">
                    <p className="text-[11px] sm:text-sm font-semibold text-yellow-400 mb-1.5">
                      🚫 {label} que nunca saíram no recorte ({res.nuncaSorteados.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {res.nuncaSorteados.slice(0, 60).map((k) => (
                        <Badge
                          key={k}
                          variant="outline"
                          className="text-[9px] font-mono border-yellow-500/25 bg-black/40 text-yellow-200/80"
                        >
                          {rotuloItem(mod, k)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
};

export default AnaliseAvancada;
